// src/sections/SeoRewrite.jsx
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { COLORS, RADII, SEMANTIC, FONT_SANS } from '../constants/theme.js';

const MOCK_RESULTS = {
  title: { original: 'Come migliorare la produttività aziendale', rewrite: 'Produttività aziendale: 7 strategie comprovate per il 2026' },
  meta: { original: 'Scopri i nostri consigli per migliorare la produttività.', rewrite: 'Scopri 7 strategie comprovate per aumentare la produttività aziendale nel 2026. Guida pratica con esempi reali e ROI misurabile.' },
  headings: [
    { original: 'I nostri consigli', rewrite: 'Strategie di produttività aziendale' },
    { original: 'Cosa fare', rewrite: 'Come implementare un framework di produttività' },
    { original: 'Conclusioni', rewrite: 'Prossimi passi per la tua azienda' },
  ],
  keywords: ['produttività aziendale', 'efficienza operativa', 'ottimizzazione processi', 'gestione tempo', 'KPI performance'],
  score: { before: 38, after: 84 },
};

const card = {
  background: COLORS.surface,
  borderRadius: RADII.control,
  border: `1px solid ${COLORS.border}`,
  padding: '20px',
  boxShadow: `0 1px 3px ${COLORS.border}`,
  marginBottom: '16px',
};

const labelStyle = {
  fontSize: '11px',
  color: COLORS.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: 500,
  marginBottom: '6px',
};

function ScoreBadge({ value, label }) {
  const color = value >= 70 ? SEMANTIC.verde.fg : value >= 40 ? SEMANTIC.attenzione.fg : SEMANTIC.errore.fg;
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '36px', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '4px' }}>{label}</div>
    </div>
  );
}

function DiffRow({ label, original, rewrite }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '13px', padding: '8px 12px', background: SEMANTIC.errore.bg, borderLeft: `3px solid ${SEMANTIC.errore.fg}`, borderRadius: RADII.controlInner, color: COLORS.textSecondary, marginBottom: '6px', textDecoration: 'line-through', wordBreak: 'break-word' }}>{original}</div>
      <div style={{ fontSize: '13px', padding: '8px 12px', background: SEMANTIC.verde.bg, borderLeft: `3px solid ${SEMANTIC.verde.fg}`, borderRadius: RADII.controlInner, color: COLORS.ink, fontWeight: 500, wordBreak: 'break-word' }}>{rewrite}</div>
    </div>
  );
}

export default function SeoRewrite() {
  const { t } = useTranslation('dashboard');
  const { isSuperadmin, adminMode } = useAuth();
  const [url, setUrl] = useState('');
  const [showResults, setShowResults] = useState(true);
  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;

  return (
    <>
      <style>{`
        .seo-wrap { padding: 32px 40px; font-family: ${FONT_SANS}; color: ${COLORS.ink}; max-width: 960px; }
        .seo-input-row { display: flex; gap: 10px; }
        .seo-input-field { flex: 1; padding: 10px 14px; border-radius: ${RADII.controlInner}; border: 1px solid ${COLORS.borderStrong}; font-size: 13px; font-family: ${FONT_SANS}; outline: none; min-width: 0; }
        .seo-score-row { display: flex; justify-content: center; gap: 60px; align-items: center; padding: 28px; }
        @media (max-width: 600px) {
          .seo-wrap { padding: 20px 16px; }
          .seo-input-row { flex-direction: column; }
          .seo-score-row { gap: 24px; padding: 20px 12px; }
        }
      `}</style>
      <div className="seo-wrap">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>SEO Rewrite</h1>
          <span style={{ padding: '3px 10px', borderRadius: '20px', background: SEMANTIC.attenzione.bg, color: SEMANTIC.attenzione.fg, fontSize: '11px', fontWeight: 600 }}>WIP</span>
        </div>
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '16px' }}>
          {t('seo.subtitle')}
        </p>
        <div style={{ padding: '10px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.attenzione.bg, border: `1px solid ${SEMANTIC.attenzione.bg}`, fontSize: '12px', color: SEMANTIC.attenzione.fg, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>⚠️</span> {t('seo.mockWarning')}
        </div>

        {/* Input */}
        <div style={card}>
          <div style={labelStyle}>{t('seo.urlLabel')}</div>
          <div className="seo-input-row">
            <input
              type="text"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://esempio.it/blog/articolo"
              className="seo-input-field"
            />
            <button
              onClick={() => setShowResults(true)}
              style={{ padding: '10px 20px', borderRadius: RADII.controlInner, border: 'none', background: '#0A66C2', color: COLORS.surface, fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t('seo.analyze')}
            </button>
          </div>
        </div>

        {showResults && (
          <>
            {/* Score */}
            <div style={card}>
              <div className="seo-score-row">
                <ScoreBadge value={MOCK_RESULTS.score.before} label={t('seo.scoreCurrent')} />
                <div style={{ fontSize: '24px', color: COLORS.textSecondary }}>→</div>
                <ScoreBadge value={MOCK_RESULTS.score.after} label={t('seo.scoreOptimized')} />
              </div>
            </div>

            {/* Title & Meta */}
            <div style={card}>
              <DiffRow label={t('seo.titleTag')} original={MOCK_RESULTS.title.original} rewrite={MOCK_RESULTS.title.rewrite} />
              <DiffRow label={t('seo.metaDescription')} original={MOCK_RESULTS.meta.original} rewrite={MOCK_RESULTS.meta.rewrite} />
            </div>

            {/* Headings */}
            <div style={card}>
              <div style={{ ...labelStyle, marginBottom: '12px' }}>{t('seo.headings')}</div>
              {MOCK_RESULTS.headings.map((h, i) => (
                <DiffRow key={i} label={`H${i + 2}`} original={h.original} rewrite={h.rewrite} />
              ))}
            </div>

            {/* Keywords */}
            <div style={card}>
              <div style={labelStyle}>{t('seo.suggestedKeywords')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {MOCK_RESULTS.keywords.map((kw, i) => (
                  <span key={i} style={{ padding: '5px 14px', borderRadius: '20px', background: 'rgba(10,102,194,0.08)', color: '#0A66C2', fontSize: '12px', fontWeight: 500 }}>{kw}</span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
